import i18n from '../i18n';
import { getApiErrorMessage } from '../utils/apiError';

export type AnalyticsErrorContext = 'default' | 'imid';

/** imID result codes → English i18n keys (title). */
const IMID_ERROR_I18N_KEYS: Record<string, string> = {
    '2000': 'imIdVerification.incorrectSSN',
    '2001': 'imIdVerification.numberNotLinked',
    '2002': 'imIdVerification.requestCancelled',
    '2003': 'imIdVerification.connectionLost',
    '9999': 'imIdVerification.generalErrorTitle',
};

/** Codes without a dedicated `error{code}` translation entry. */
const FALLBACK_ENGLISH: Record<string, string> = {
    SumSubError: 'Biometric verification failed',
    SumSubExpired: 'Biometric verification session expired',
    UNKNOWN_ERROR: 'An error has occurred',
};

function stripErrorPrefix(code: string): string {
    return code.replace(/^error/i, '');
}

function translateKey(key: string): string | null {
    if (!i18n.exists(key, { lng: 'en' })) {
        return null;
    }
    return i18n.getFixedT('en')(key);
}

/**
 * English UI title for a backend / client error code (e.g. `5002` → "Existing customer").
 * Use `context: 'imid'` for imID result codes (they overlap numeric ranges with other errors).
 */
export function getEnglishErrorMessage(
    errorCode: string,
    context: AnalyticsErrorContext = 'default'
): string {
    const raw = String(errorCode ?? '').trim() || 'UNKNOWN_ERROR';
    const code = stripErrorPrefix(raw);

    if (context === 'imid') {
        const imidKey = IMID_ERROR_I18N_KEYS[code];
        if (imidKey) {
            const imidMessage = translateKey(imidKey);
            if (imidMessage) {
                return imidMessage;
            }
        }
    }

    const candidates = [
        `error${code}`,
        /^[A-Za-z]/.test(code) ? `error${code}` : null,
        raw.startsWith('error') ? raw : null,
    ].filter((key, index, arr): key is string =>
        Boolean(key) && arr.indexOf(key) === index
    );

    for (const key of candidates) {
        const message = translateKey(key);
        if (message) {
            return message;
        }
    }

    if (FALLBACK_ENGLISH[code] || FALLBACK_ENGLISH[raw]) {
        return FALLBACK_ENGLISH[code] ?? FALLBACK_ENGLISH[raw];
    }

    return translateKey('error3012') ?? code;
}

/**
 * Builds GA4 error fields:
 * - `error_code` — backend / client code without `error` prefix (e.g. `5002`)
 * - `error_message` — English translation of that code
 */
export function resolveAnalyticsError(options: {
    error?: unknown;
    errorCode?: string;
    fallback?: string;
    context?: AnalyticsErrorContext;
}): { error_code: string; error_message: string } {
    const fallback = options.fallback ?? 'UNKNOWN_ERROR';
    const raw =
        options.errorCode ??
        (options.error != null
            ? getApiErrorMessage(options.error, fallback)
            : fallback);
    const error_code = stripErrorPrefix(String(raw).trim() || fallback);

    return {
        error_code,
        error_message: getEnglishErrorMessage(
            error_code,
            options.context ?? 'default'
        ),
    };
}
